// src/components/SphereMenu.tsx
import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Sphere, OrbitControls, Html } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import * as THREE from 'three';
import { s } from 'framer-motion/dist/types.d-CtuPurYT';

// MenuItemインターフェースをエクスポートして親コンポーネントでも使えるようにします
export interface MenuItem {
  id: string;
  name: string;
  position: [number, number, number];
  path: string;
  componentName?: string; // オプションでコンポーネント名を追加
}

// このデータは外部からpropsとして渡すのがより柔軟ですが、ここでは内部に保持します
const menuItemsData: MenuItem[] = [
  { id: 'home', name: 'HOME', position: [0, 2, 0], path: '/home' },
  { id: 'philosophy', name: 'PHILOSOPHY', position: [2, 0, 0], path: '/philosophy' },
  { id: 'works', name: 'WORKS', position: [-2, 0, 0], path: '/works' },
  { id: 'profile', name: 'PROFILE', position: [0, -2, 0], path: '/profile' },
  { id: 'recruitment', name: 'RECRUITMENT', position: [0, 0, -2], path: '/recruitment' },
  { id: 'contact', name: 'CONTACT', position: [0, 0, 2], path: '/contact' },
  { id: 'blog', name: 'BLOG', position: [1.5, 1.5, 0], path: '/blog' },
];

interface MenuPointProps {
  item: MenuItem;
  onClick: () => void;
  isSelected: boolean; // 現在選択されているかどうかを示すプロパティを追加
}

const MenuPoint: React.FC<MenuPointProps> = ({ item, onClick, isSelected }) => {
  const [hovered, setHovered] = useState(false);
  const MenuLabel = styled.div`
    color: #e0e0e0;
    cursor: pointer;
    padding: 10px 15px;
    background-color: rgba(50, 50, 50, 0.5);
    border: 1px solid rgba(200, 200, 200, 0.3);
    border-radius: 8px;
    font-weight: bold;
    font-size: 16px;
    white-space: nowrap;
    box-shadow: none;
    z-index: 1;
    :hover {
      color: cyan;
      background-color: rgba(0, 200, 200, 0.3);
      border: 1px solid cyan;
      box-shadow: 0 0 15px cyan;
    }
    @media (max-width: 1024px) {
      font-size: 12px; /* タブレット向けにフォントサイズを調整 */
      padding: 6px 10px; /* タブレット向けにパディングを調整 */
    }
  `;
  return (
    <Html position={item.position} center>
      <motion.div
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
        style={{
          transition: "all 0.5s ease-in-out",
          opacity: isSelected ? 0 : 1, // 選択されている場合は不透明
        }}
      >
        <MenuLabel>
          {item.name}
        </MenuLabel>
      </motion.div>
    </Html>
  );
};

interface RotatingSphereProps {
  children: React.ReactNode;
  isRotating: boolean;
  targetQuaternion: THREE.Quaternion | null;
}

const RotatingSphere: React.FC<RotatingSphereProps> = ({ children, isRotating, targetQuaternion }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    if (!meshRef.current) return;
    if (isRotating) {
      meshRef.current.rotation.y += 0.003;
      meshRef.current.rotation.x += 0.002;
    } else if (targetQuaternion) {
      // 目標のクォータニオンとの角度差を計算
      const angle = meshRef.current.quaternion.angleTo(targetQuaternion);

      // ある程度近づいたら直接セットし、それ以外はslerpする
      if (angle > 0.001) { // この閾値は調整可能
        meshRef.current.quaternion.slerp(targetQuaternion, 0.1); // slerp係数を少し上げてみる (0.05 -> 0.1)
      } else {
        meshRef.current.quaternion.copy(targetQuaternion); // ほぼ到達したら正確にコピー
        // targetQuaternion を null に戻す処理を親で行うか、
        // ここで何らかのフラグを立てて slerp を停止する
        // 現在の設計では activeItemPath が変わるまで targetQuaternion は維持される
      }
    }
  });
  return (
    <Sphere ref={meshRef} args={[2, 16, 16]}>
      <meshStandardMaterial color="#fff" wireframe={true} transparent opacity={0.6} />
      {children}
    </Sphere>
  );
};

interface SphereMenuProps {
  onItemSelected: (item: MenuItem) => void; // 親に選択されたアイテムを通知
  activeItemPath: string | null;           // 親から現在アクティブなアイテムのパスを受け取る
}

const SphereCanvasStyle = styled.div`
  @media (max-width: 1124px) {
    /** タブレットでは画面上部に表示 */
    top: 0;
    left: 0;
    width: 100vw;
    max-height: 45vh;
    z-index: 5;
    overflow: hidden;
  }
  position: absolute;
  top: 5vh;
  left: 0vw;
  width: 40vw;
  height: 90vh;
  z-index: 5;
  `;

const SphereMenu: React.FC<SphereMenuProps> = ({ onItemSelected, activeItemPath }) => {
  const navigate = useNavigate();
  const [isRotating, setIsRotating] = useState(true);
  const [targetQuaternion, setTargetQuaternion] = useState<THREE.Quaternion | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null); // 選択されたメニューのIDを管理

  useEffect(() => {
    if (activeItemPath) {
      const activeItem = menuItemsData.find(item => item.path === activeItemPath);
      if (activeItem) {
        setIsRotating(false); // 回転を停止

        // 選択されたアイテムの位置を正規化
        const itemLocalPosNormalized = new THREE.Vector3(...activeItem.position).normalize();

        // カメラの方向を基準にするため、ワールド座標系の正面方向 (0, 0, -1) を使用
        const cameraFrontDirection = new THREE.Vector3(0, 0, -1);

        // クォータニオンを計算
        const newTargetQuaternion = new THREE.Quaternion();
        newTargetQuaternion.setFromUnitVectors(cameraFrontDirection, itemLocalPosNormalized);

        // クォータニオンを設定
        //setTargetQuaternion(newTargetQuaternion);
      } else {
        setIsRotating(true);
        setTargetQuaternion(null);
        setSelectedItemId(null); // 選択されたアイテムが見つからない場合はリセット
      }
    } else {
      setIsRotating(true);
      setTargetQuaternion(null);
      setSelectedItemId(null); // activeItemPath が null の場合はリセット
    }
  }, [activeItemPath]);

  const handleLocalMenuClick = (item: MenuItem) => {
    setSelectedItemId(item.id); // 選択されたメニューのIDを設定
    onItemSelected(item); // 親コンポーネントに選択を通知
    navigate(item.path);  // ナビゲーションを実行
  };

  return (
    <SphereCanvasStyle>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 75 }}
      >
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={1.2} />
        <RotatingSphere isRotating={isRotating} targetQuaternion={targetQuaternion}>
          {menuItemsData.map((item) => (
            <MenuPoint
              key={item.id}
              item={item}
              onClick={() => handleLocalMenuClick(item)}
              isSelected={selectedItemId === item.id} // 現在選択されているかどうかを渡す
            />
          ))}
        </RotatingSphere>
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          enableRotate={isRotating}
          target={[0, 0, 0]}
        />
      </Canvas>
    </SphereCanvasStyle>
  );
};

export default SphereMenu;